import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "jc_pwa_dismissed_at";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

/**
 * Floating install prompt for Chrome/Edge/Android (via beforeinstallprompt)
 * and a simple iOS instruction bubble (Safari doesn't expose the event).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: no beforeinstallprompt, so show a manual hint after a short delay
    if (isIOS()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[60] bg-white border border-slate-200 rounded-md shadow-lg p-4 flex items-start gap-3 no-print"
      data-testid="pwa-install-prompt"
    >
      <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800 shrink-0">
        <Download size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base font-black tracking-tight text-slate-900">
          Install Just Compare
        </div>
        {iosHint ? (
          <p className="mt-1 text-xs text-slate-600 leading-snug">
            Tap the <span className="font-semibold">Share</span> icon in Safari, then{" "}
            <span className="font-semibold">Add to Home Screen</span> to launch this like a native app.
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-600 leading-snug">
            Get a one-tap icon on your home screen, faster launch and offline-ready shell for
            warehouse scanning.
          </p>
        )}
        <div className="mt-2 flex gap-2">
          {!iosHint && deferred && (
            <Button
              size="sm"
              onClick={install}
              data-testid="pwa-install-btn"
              className="h-8 rounded-sm bg-blue-800 hover:bg-blue-900 text-white text-xs font-semibold"
            >
              Install
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={dismiss}
            data-testid="pwa-dismiss-btn"
            className="h-8 rounded-sm text-xs font-semibold"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-slate-400 hover:text-slate-600 shrink-0"
      >
        <X size={16} />
      </button>
    </div>
  );
}
